/**
 * Mastery Scoring Service
 *
 * Calculates mastery scores based on attempt history with recency weighting.
 * Implements spaced repetition scheduling at 1/3/7/30 day intervals.
 */

import { LessonProgress, LessonMastery, MasteryLevel, ReviewSchedule } from '@/types/models';

/**
 * Calculate mastery score from attempt history
 *
 * Algorithm:
 * - Recent attempts weighted more heavily (exponential decay)
 * - Perfect scores boost mastery
 * - Multiple attempts show engagement but may indicate difficulty
 * - Consistency over time is rewarded
 */
export function calculateMasteryScore(
  _progress: LessonProgress,
  allAttempts: { score: number; timestamp: number }[]
): number {
  if (allAttempts.length === 0) return 0;

  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  // Weight recent attempts more heavily (exponential decay with half-life of 7 days)
  const weightedSum = allAttempts.reduce((sum, attempt) => {
    const ageInDays = (now - attempt.timestamp) / oneDay;
    const weight = Math.exp(-ageInDays / 7); // Half-life of 7 days
    return sum + attempt.score * weight;
  }, 0);

  const totalWeight = allAttempts.reduce((sum, attempt) => {
    const ageInDays = (now - attempt.timestamp) / oneDay;
    return sum + Math.exp(-ageInDays / 7);
  }, 0);

  const baseScore = weightedSum / totalWeight;

  // Bonus for consistency (if last 3 attempts all above 80%)
  const recentAttempts = allAttempts.slice(-3);
  const allRecent80Plus = recentAttempts.length >= 3 &&
    recentAttempts.every(a => a.score >= 80);
  const consistencyBonus = allRecent80Plus ? 5 : 0;

  // Bonus for perfect score on most recent attempt
  const perfectBonus = allAttempts[allAttempts.length - 1].score === 100 ? 5 : 0;

  // Cap at 100
  return Math.min(100, Math.round(baseScore + consistencyBonus + perfectBonus));
}

/**
 * Determine mastery level from score
 */
export function getMasteryLevel(masteryScore: number): MasteryLevel {
  if (masteryScore >= 90) return 'master';
  if (masteryScore >= 75) return 'advanced';
  if (masteryScore >= 50) return 'intermediate';
  return 'novice';
}

/**
 * Calculate trend from recent attempts
 */
export function calculateTrend(
  allAttempts: { score: number; timestamp: number }[]
): 'improving' | 'stable' | 'declining' {
  if (allAttempts.length < 3) return 'stable';

  const recent5 = allAttempts.slice(-5);
  const firstHalf = recent5.slice(0, Math.ceil(recent5.length / 2));
  const secondHalf = recent5.slice(Math.ceil(recent5.length / 2));

  const firstAvg = firstHalf.reduce((sum, a) => sum + a.score, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, a) => sum + a.score, 0) / secondHalf.length;

  const diff = secondAvg - firstAvg;

  if (diff > 5) return 'improving';
  if (diff < -5) return 'declining';
  return 'stable';
}

/**
 * Create or update lesson mastery record
 */
export function updateLessonMastery(
  progress: LessonProgress,
  allAttempts: { score: number; timestamp: number }[],
  userId: string
): LessonMastery {
  const masteryScore = calculateMasteryScore(progress, allAttempts);
  const masteryLevel = getMasteryLevel(masteryScore);
  const trend = calculateTrend(allAttempts);

  const correctAttempts = allAttempts.filter(a => a.score >= 80).length;
  const averageScore = allAttempts.reduce((sum, a) => sum + a.score, 0) / allAttempts.length;

  return {
    lessonId: progress.lessonId,
    userId,
    masteryScore,
    masteryLevel,
    totalAttempts: allAttempts.length,
    correctAttempts,
    averageScore: Math.round(averageScore),
    lastAttemptScore: allAttempts[allAttempts.length - 1].score,
    trend,
    updatedAt: Date.now(),
  };
}

/**
 * Spaced Repetition Intervals (in days)
 */
const REVIEW_INTERVALS = [1, 3, 7, 30];

/**
 * Calculate next review date based on current review number
 */
export function calculateNextReview(
  currentReviewNumber: number,
  masteryScore: number
): { nextReviewDate: number; intervalDays: number } {
  // If mastery is low, repeat current interval
  if (masteryScore < 70 && currentReviewNumber > 0) {
    const intervalDays = REVIEW_INTERVALS[currentReviewNumber - 1];
    return {
      nextReviewDate: Date.now() + intervalDays * 24 * 60 * 60 * 1000,
      intervalDays,
    };
  }

  // Progress to next interval
  const nextIndex = Math.min(currentReviewNumber, REVIEW_INTERVALS.length - 1);
  const intervalDays = REVIEW_INTERVALS[nextIndex];

  return {
    nextReviewDate: Date.now() + intervalDays * 24 * 60 * 60 * 1000,
    intervalDays,
  };
}

/**
 * Create initial review schedule when lesson is first completed
 */
export function createInitialReviewSchedule(
  lessonId: string,
  userId: string,
  masteryScore: number
): ReviewSchedule {
  const { nextReviewDate, intervalDays } = calculateNextReview(0, masteryScore);

  return {
    lessonId,
    userId,
    nextReviewDate,
    intervalDays,
    reviewNumber: 0,
    lastReviewedAt: Date.now(),
    masteryScore,
    isCompleted: false,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Update review schedule after a review is completed
 */
export function updateReviewSchedule(
  currentSchedule: ReviewSchedule,
  newMasteryScore: number
): ReviewSchedule {
  const nextReviewNumber = currentSchedule.reviewNumber + 1;
  const { nextReviewDate, intervalDays } = calculateNextReview(
    nextReviewNumber,
    newMasteryScore
  );

  // Mark as completed if we've done all 4 reviews (1, 3, 7, 30 days)
  const isCompleted = nextReviewNumber >= REVIEW_INTERVALS.length;

  return {
    ...currentSchedule,
    nextReviewDate,
    intervalDays,
    reviewNumber: nextReviewNumber,
    lastReviewedAt: Date.now(),
    masteryScore: newMasteryScore,
    isCompleted,
    updatedAt: Date.now(),
  };
}

/**
 * Check if a review is due (within 1 day of scheduled date)
 */
export function isReviewDue(schedule: ReviewSchedule): boolean {
  const now = Date.now();
  const oneDayInMs = 24 * 60 * 60 * 1000;

  return !schedule.isCompleted && now >= (schedule.nextReviewDate - oneDayInMs);
}

/**
 * Get reviews due for a user
 */
export function filterDueReviews(schedules: ReviewSchedule[]): ReviewSchedule[] {
  return schedules.filter(isReviewDue);
}
